import { Box, Checkbox, Typography, useTheme, alpha } from "@mui/material";
import { DepthBar } from "./DepthBar";
import { formatInr, formatPct } from "../holdGridConfig";

/**
 * One holding, rendered for narrow screens where the 7-column grid cannot fit.
 *
 * Carries every fact the desktop grid shows — symbol, qty, buy price, invested,
 * current price, P/L with %, and order-book depth — in four lines instead of one
 * 920px-wide row, so nothing truncates and nothing scrolls sideways.
 *
 * The whole card is the drill-in target (the grid's double-click doesn't exist on
 * touch); the checkbox stops propagation so selecting never opens the dialog.
 */
export const HoldingCard = ({ row, selected, onToggle, onOpen }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  // profitLoss null = no quote for this holding: shown as "No price", not as 0.
  const priced = row.profitLoss != null;
  const pl = Number(row.profitLoss) || 0;
  const pct = Number(row.plnPercentage) || 0;
  const plColor =
    pl > 0 ? theme.palette.success.main : pl < 0 ? theme.palette.error.main : theme.palette.text.secondary;
  const arrow = pct > 0 ? "▲" : pct < 0 ? "▼" : "•";

  const handleOpen = () => onOpen(row);

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`${row.symbol} — open buy performance`}
      onClick={handleOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOpen();
        }
      }}
      sx={{
        cursor: "pointer",
        borderRadius: 2,
        p: 1.25,
        border: `1px solid ${selected ? alpha(primary, 0.55) : alpha(primary, 0.14)}`,
        bgcolor: selected ? alpha(primary, 0.07) : theme.palette.background.paper,
        boxShadow: selected ? `0 0 0 1px ${alpha(primary, 0.35)}` : `0 2px 10px ${alpha("#000", 0.04)}`,
        transition: "background-color 0.15s ease, border-color 0.15s ease",
        "&:active": { bgcolor: alpha(primary, 0.12) },
        "&:focus-visible": { outline: `2px solid ${primary}`, outlineOffset: 2 },
      }}
    >
      {/* Line 1 — selection, symbol, P/L amount */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Checkbox
          size="small"
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggle(row.tradeId)}
          inputProps={{ "aria-label": `Select ${row.symbol}` }}
          sx={{ p: 0.5, ml: -0.5 }}
        />
        <Typography
          noWrap
          title={row.symbol}
          sx={{ fontWeight: 800, fontSize: "0.9rem", flex: 1, minWidth: 0, letterSpacing: "-0.01em" }}
        >
          {row.symbol}
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", color: plColor, whiteSpace: "nowrap" }}>
          {priced ? formatInr(pl) : "No price"}
        </Typography>
      </Box>

      {/* Line 2 — position, and the % as a pill so gain/loss reads at a glance */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 3.5, mt: 0.25 }}>
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", flex: 1, minWidth: 0 }} noWrap>
          {row.qty} × {formatInr(row.buyPrice)}
        </Typography>
        <Box
          component="span"
          sx={{
            px: 0.75,
            py: 0.125,
            borderRadius: 999,
            fontSize: "0.7rem",
            fontWeight: 800,
            whiteSpace: "nowrap",
            bgcolor: alpha(plColor, 0.14),
            color: plColor,
          }}
        >
          {priced ? `${arrow} ${formatPct(Math.abs(pct))}` : "—"}
        </Box>
      </Box>

      {/* Line 3 — the two money figures, side by side */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 1,
          mt: 1,
          pt: 1,
          borderTop: `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
        }}
      >
        {[
          { k: "Invested", v: formatInr(row.invested) },
          { k: "Value", v: formatInr(row.marketValue ?? Number(row.qty) * Number(row.marketPrice)) },
        ].map(({ k, v }) => (
          <Box key={k} sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.63rem", color: "text.secondary", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              {k}
            </Typography>
            <Typography noWrap title={v} sx={{ fontSize: "0.8rem", fontWeight: 700 }}>
              {v}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Line 4 — order-book depth, the one column that has no text form */}
      <Box sx={{ mt: 0.9 }}>
        <Typography sx={{ fontSize: "0.63rem", color: "text.secondary", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", mb: 0.4 }}>
          Depth · C.Price {formatInr(row.marketPrice)}
        </Typography>
        <DepthBar demand={row.totalDemand} supply={row.totalSupply} height={6} showLabels />
      </Box>
    </Box>
  );
};

export default HoldingCard;
